import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Trash2, ClipboardList, StickyNote, Pencil, Printer, Navigation, Route, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatApiError, mapsUrl, routeUrl, stopOf, toISODate } from "@/lib/api";
import { EventDialog } from "@/components/EventDialog";
import { PrintWeek } from "@/components/PrintWeek";

const startOfWeek = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

function Entry({ e, onDelete, onEdit }) {
  const isAppt = e.kind !== "event";
  const isDel = e.kind === "delivery";
  const body = (
    <div className={`rounded-xl border p-3 text-sm min-w-0 overflow-hidden ${isDel ? "bg-sky-50 border-sky-200" : isAppt ? "bg-amber-50 border-amber-200" : "bg-white border-[var(--border)]"}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-mono text-xs font-semibold text-[var(--muted)]">{e.time || "—"}</p>
        {isDel ? <Truck className="h-3.5 w-3.5 text-sky-700" /> : isAppt ? <ClipboardList className="h-3.5 w-3.5 text-amber-700" /> : <StickyNote className="h-3.5 w-3.5 text-[var(--muted)]" />}
      </div>
      <p className="font-semibold leading-snug mt-1 break-words">{e.title}</p>
      {e.description && <p className="text-xs text-[var(--muted)] mt-1 whitespace-pre-line break-words">{e.description}</p>}
      {isAppt && !isDel && <p className="text-xs text-amber-800 mt-1">{e.assigned_name ? `Assegnato a ${e.assigned_name}` : "Non assegnato"}</p>}
      {isDel && e.phone && <p className="text-xs mt-1">Tel: {e.phone}</p>}
      {isDel && e.staff_notes && <p className="text-xs text-sky-800 mt-0.5 break-words">Note: {e.staff_notes}</p>}
      {isDel && e.balance > 0 && <p className="text-xs font-semibold text-[var(--primary-hover)] mt-0.5" data-testid={`agenda-delivery-balance-${e.id}`}>Saldo da incassare: {eur(e.balance)}</p>}
      {isAppt && e.floor && <p className="text-xs text-[var(--muted)] mt-0.5 break-words">Piano: {e.floor}</p>}
      {!isAppt && (
        <div className="mt-2 space-y-1">
          <p className="text-[11px] text-[var(--muted)] truncate">{e.created_by}</p>
          <div className="flex gap-1 -ml-1.5">
            <button onClick={() => onEdit(e)} className="h-8 w-8 grid place-items-center rounded-full text-[var(--muted)] hover:bg-[var(--surface-2)] hover:text-[var(--ink)]" data-testid={`edit-event-${e.id}`} aria-label="Modifica"><Pencil className="h-3.5 w-3.5" /></button>
            <button onClick={() => onDelete(e)} className="h-8 w-8 grid place-items-center rounded-full text-[var(--muted)] hover:bg-red-50 hover:text-red-600" data-testid={`delete-event-${e.id}`} aria-label="Elimina"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      )}
    </div>
  );
  if (isAppt) {
    return (
      <div className="space-y-1">
        <Link to={isDel ? `/impegnative/${e.id}` : `/appuntamenti/${e.id}`} data-testid={isDel ? `agenda-delivery-${e.id}` : `agenda-appt-${e.id}`} className="block card-lift">{body}</Link>
        {e.maps && (
          <a href={e.maps} target="_blank" rel="noreferrer" data-testid={isDel ? `agenda-delivery-navigate-${e.id}` : `agenda-navigate-${e.id}`}
            className="flex items-center justify-center gap-1.5 h-9 rounded-lg bg-[var(--ink)] text-white text-xs font-semibold hover:opacity-90">
            <Navigation className="h-3.5 w-3.5" /> Naviga
          </a>
        )}
      </div>
    );
  }
  return <div data-testid={`agenda-event-${e.id}`}>{body}</div>;
}

export default function Agenda() {
  const [week, setWeek] = useState(() => startOfWeek(new Date()));
  const [events, setEvents] = useState([]);
  const [appts, setAppts] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(week, i)), [week]);
  const start = toISODate(days[0]);
  const end = toISODate(days[6]);
  const today = toISODate(new Date());

  const load = useCallback(async () => {
    try {
      const [ev, ap, dv] = await Promise.all([api.get("/events", { params: { start, end } }), api.get("/appointments"), api.get("/deliveries", { params: { start, end } }).catch(() => ({ data: [] }))]);
      setEvents(ev.data);
      setDeliveries(dv.data);
      setAppts(ap.data.filter((a) => a.agenda_date && a.agenda_date >= start && a.agenda_date <= end).map((a) => ({ ...a, date: a.agenda_date, time: a.agenda_time })));
      setLoaded(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [start, end]);

  useEffect(() => {
    setLoaded(false);
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const byDay = (iso) => [
    ...appts.filter((a) => a.date === iso).map((a) => ({ kind: "appt", id: a.id, time: a.time, title: `${"pickup_date" in a ? "Ritiro" : "Preventivo"}: ${a.client_name}`, description: a.client_address, assigned_name: a.assigned_name, floor: a.floor, maps: mapsUrl(a), stop: stopOf(a) })),
    ...events.filter((e) => e.date === iso).map((e) => ({ ...e, kind: "event" })),
    ...deliveries.filter((d) => d.date === iso).map((d) => ({ kind: "delivery", id: d.id, time: d.time_slot, title: `Consegna ${d.number}: ${d.customer_name}`, description: d.address,
      phone: d.phone, floor: d.floor_info, staff_notes: d.staff_notes, balance: d.balance, maps: mapsUrl({ client_address: d.address }), stop: d.address || null })),
  ].sort((a, b) => (a.time || "99").localeCompare(b.time || "99"));

  const removeEvent = async (e) => {
    if (!window.confirm("Eliminare questo impegno?")) return;
    try {
      await api.delete(`/events/${e.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    load();
  };

  return (
    <>
    <div className="space-y-8 no-print">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 fade-up">
        <div>
          <p className="eyebrow">Roadmap del team</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Agenda</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setWeek(addDays(week, -7))} className="btn-secondary h-10 w-10 p-0" data-testid="prev-week-button" aria-label="Settimana precedente"><ChevronLeft className="h-5 w-5" /></button>
          <button onClick={() => setWeek(startOfWeek(new Date()))} className="btn-secondary h-10 px-4" data-testid="today-button">Oggi</button>
          <button onClick={() => setWeek(addDays(week, 7))} className="btn-secondary h-10 w-10 p-0" data-testid="next-week-button" aria-label="Settimana successiva"><ChevronRight className="h-5 w-5" /></button>
          <button onClick={() => setDialog({ date: today })} className="btn-primary h-10 px-4" data-testid="add-event-button"><Plus className="h-5 w-5" /> Impegno</button>
          <button onClick={() => window.print()} className="btn-secondary h-10 px-4" data-testid="print-week-button"><Printer className="h-5 w-5" /> Stampa</button>
        </div>
      </div>
      <p className="text-sm text-[var(--muted)]" data-testid="week-range">
        {days[0].toLocaleDateString("it-IT", { day: "numeric", month: "long" })} – {days[6].toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="week-grid">
        {days.map((d) => {
          const iso = toISODate(d);
          const list = byDay(iso);
          const stops = list.filter((e) => e.kind !== "event" && e.stop).map((e) => e.stop);
          const isToday = iso === today;
          return (
            <div key={iso} className={`rounded-2xl p-3 min-h-[120px] md:min-h-[320px] ${isToday ? "bg-[var(--ink)]/[0.04] ring-2 ring-[var(--primary)]" : "bg-[var(--surface-2)]"}`} data-testid={`day-column-${iso}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-[var(--muted)]">{d.toLocaleDateString("it-IT", { weekday: "short" })}</p>
                  <p className={`font-heading text-xl font-bold ${isToday ? "text-[var(--primary-hover)]" : ""}`}>{d.getDate()}</p>
                </div>
                <button onClick={() => setDialog({ date: iso })} className="h-7 w-7 rounded-full grid place-items-center hover:bg-white" data-testid={`add-event-${iso}`} aria-label="Aggiungi"><Plus className="h-4 w-4" /></button>
              </div>
              {stops.length >= 2 && (
                <a href={routeUrl(stops)} target="_blank" rel="noreferrer" data-testid={`day-route-${iso}`}
                  className="mb-3 flex items-center justify-center gap-1.5 min-h-9 py-2 px-2 rounded-lg bg-[var(--primary)] text-white text-xs font-semibold text-center hover:bg-[var(--primary-hover)]">
                  <Route className="h-4 w-4 shrink-0" /> Percorso ({stops.length} tappe)
                </a>
              )}
              <div className="space-y-2">
                {!loaded && <div className="h-10 rounded-xl bg-white/70 animate-pulse" data-testid="agenda-loading" />}
                {loaded && list.length === 0 && <p className="text-xs text-[var(--muted)]">Nessun impegno</p>}
                {list.map((e) => <Entry key={`${e.kind}-${e.id}`} e={e} onDelete={removeEvent} onEdit={(ev) => setDialog(ev)} />)}
              </div>
            </div>
          );
        })}
      </div>
      <EventDialog event={dialog} onClose={() => setDialog(null)} onSaved={load} />
    </div>
    <PrintWeek appts={appts} deliveries={deliveries} start={start} end={end} />
    </>
  );
}
